import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { apiFetch } from '@/lib/api/client';
import { JOURNAL_TYPES, type JournalType } from '@/pages/safe/safeJournalApi';

/**
 * 사고 · 시정조치 사례 모음 — 백엔드 SafeCaseController(DOC-136 · 2026-10-28). 읽기만.
 * 배정 현장 밖의 사례는 서버가 현장을 가려 보낸다(anonymous · siteId/siteName/refId 없음).
 */

export type CaseKind = 'ACCIDENT' | 'NEAR_MISS' | 'CORRECTIVE' | 'FINDING';

export const CASE_KINDS: Array<{ kind: CaseKind; label: string; tone: string }> = [
  { kind: 'ACCIDENT', label: '사고', tone: 'badge-red' },
  { kind: 'NEAR_MISS', label: '아차사고', tone: 'badge-yellow' },
  { kind: 'CORRECTIVE', label: '부적합/시정조치', tone: 'badge-blue' },
  { kind: 'FINDING', label: '점검 지적', tone: 'badge-gray' },
];

export const kindOf = (kind: string) => CASE_KINDS.find((one) => one.kind === kind) ?? { kind, label: kind, tone: 'badge-gray' };

export type SafeCase = {
  key: string;
  date: string | null;
  siteId: string | null;
  siteName: string | null;
  anonymous: boolean;
  kind: CaseKind;
  category: string | null;
  trade: string | null;
  place: string | null;
  what: string;
  cause: string | null;
  action: string | null;
  origin: 'old' | 'new';
  refType: string | null;
  refId: string | null;
};

export type CasePage = { total: number; page: number; size: number; rows: SafeCase[] };

export type CaseSuggestion = { key: string; date: string | null; kind: CaseKind; category: string | null; trade: string | null; line: string };

export type CaseFilter = { q: string; type: string; siteId: string | null; from: string; to: string; page: number };

// 앞머리 /api 는 apiFetch 가 붙인다(runtimeConfig.apiBaseUrl). 여기서 또 붙이면 /api/api/… 로 가 404 다(2026-11-02 운영).
const BASE = '/v1/safe/cases';

const qs = (params: Record<string, string | number | null | undefined>) =>
  new URLSearchParams(Object.entries(params).filter((e): e is [string, string | number] => e[1] !== null && e[1] !== undefined && e[1] !== '').map(([k, v]) => [k, String(v)])).toString();

export function useSafeCases(filter: CaseFilter) {
  return useQuery({
    queryKey: ['safe-cases', filter],
    queryFn: () => apiFetch<CasePage>(`${BASE}?${qs(filter)}`),
    placeholderData: keepPreviousData,
  });
}

/** TBM 특이사항에 붙는 한 줄 머리. */
export const CASE_LINE_HEAD = '1일 1사고사례: ';

/** 특이사항 글 끝에 사례 한 줄을 더한다(빈 글이면 그 줄만). */
export function appendCaseLine(content: string | null | undefined, line: string): string {
  const body = (content ?? '').replace(/\s+$/, '');
  return `${body ? `${body}\n` : ''}${CASE_LINE_HEAD}${line}`;
}

/** TBM 「1일 1사고사례」 — trades 는 고른 작업종류(「공종 - 작업」). */
export function useCaseSuggestions(trades: string[], q: string, limit = 5) {
  const params = new URLSearchParams();
  trades.forEach((one) => params.append('trade', one));
  if (q.trim()) params.set('q', q.trim());
  params.set('limit', String(limit));
  return useQuery({
    queryKey: ['safe-cases', 'suggest', trades, q.trim(), limit],
    queryFn: () => apiFetch<CaseSuggestion[]>(`${BASE}/suggest?${params.toString()}`),
    placeholderData: keepPreviousData,
  });
}

/**
 * 원본 여는 길 — 옛 기록은 보관함 창(legacy), 새 일지는 그 일지 주소(?open=), 아차사고 · 포털 시정조치는 그 현장 목록.
 * 가린 사례는 null.
 */
export function caseTarget(row: SafeCase): { legacy: { source: string; code: string } } | { path: string } | null {
  if (row.anonymous || !row.refType || !row.refId) return null;
  if (row.origin === 'old') return { legacy: { source: row.refType, code: row.refId } };
  if (!row.siteId) return null;
  const site = `/sites/${encodeURIComponent(row.siteId)}`;
  if (row.refType === 'VOICE') return { path: `${site}/near-miss` };
  if (row.refType === 'ACTION') return { path: `${site}/actions` };
  const type = JOURNAL_TYPES[row.refType as JournalType];
  return type ? { path: `${site}${type.path}?open=${encodeURIComponent(row.refId)}` } : null;
}
